import { useEffect, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import type { Product } from "../types";

export interface ProductFilters {
  categorySlug: string;
  subcategorySlug?: string;
}

export const useProducts = (filters: ProductFilters) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchProducts = async () => {
      setIsLoading(true);
      try {
        const constraints = [
          where("categorySlug", "==", filters.categorySlug),
        ];
        if (filters.subcategorySlug) {
          constraints.push(
            where("subcategorySlug", "==", filters.subcategorySlug),
          );
        }
        const snap = await getDocs(
          query(collection(db, "products"), ...constraints),
        );
        const list = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as Product[];
        if (!cancelled) setProducts(list);
      } catch {
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchProducts();
    return () => {
      cancelled = true;
    };
  }, [filters.categorySlug, filters.subcategorySlug]);

  return { products, isLoading };
};

export const useProductsByIds = (ids: string[]) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const key = ids.join(",");

  useEffect(() => {
    let cancelled = false;
    const fetchProducts = async () => {
      if (ids.length === 0) {
        setProducts([]);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const snap = await getDocs(collection(db, "products"));
        const byId = new Map(
          snap.docs.map((d) => [d.id, { id: d.id, ...d.data() } as Product]),
        );
        const ordered = ids
          .map((id) => byId.get(id))
          .filter((p): p is Product => Boolean(p));
        if (!cancelled) setProducts(ordered);
      } catch {
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchProducts();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { products, isLoading };
};
